'use client';

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { imageSrc } from '@/utils/runtimeOrg';
import packageJson from '../../../package.json';

type FooterLink = {
  title: string;
  url: string;
};

type FooterProps = {
  cfg: {
    name: string;
    logo: string;
    footer?: {
      description?: string;
      links?: FooterLink[];
      copyright?: string;
    };
  };
};

const isExternal = (url: string) =>
  /^https?:\/\//i.test(url) ||
  url.startsWith('mailto:') ||
  url.startsWith('tel:');

export default function Footer({ cfg }: FooterProps) {
  const footer = cfg.footer ?? {};
  const links: FooterLink[] = footer.links ?? [];
  const currentYear = new Date().getFullYear();
  const copyright = footer.copyright?.replace(/©\s*\d{4}/, `© ${currentYear}`);

  return (
    <footer className="bg-[#f9f9f9] text-[#1b2d2a] text-sm">
      <div className="py-8 flex flex-col lg:flex-row justify-between items-start space-y-8 lg:space-y-0 container px-4 mx-auto">
        <div className="flex flex-col space-y-4 max-w-3xl">
          <div className="flex items-center w-[200px] space-x-2">
            <Link href="/" aria-label="Home">
              <Image
                src={imageSrc(cfg.logo)}
                alt={cfg.name}
                width={148}
                height={64}
                className="w-full h-full"
                priority={false}
              />
            </Link>
          </div>

          {footer.description ? (
            <p className="text-[#1b2d2a] leading-relaxed">
              {footer.description}
            </p>
          ) : null}

          {!!links.length && (
            <div className="flex flex-wrap gap-x-6 gap-y-2 pt-2">
              {links.map((link) =>
                isExternal(link.url) ? (
                  <a
                    key={link.title}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline hover:text-[var(--color-accent)] transition-colors"
                  >
                    {link.title}
                  </a>
                ) : (
                  <Link
                    key={link.title}
                    href={link.url}
                    className="hover:underline hover:text-[var(--color-accent)] transition-colors"
                  >
                    {link.title}
                  </Link>
                )
              )}
            </div>
          )}
        </div>
      </div>

      <hr className="border-t border-gray-200 mx-auto" />

      <div className="container mx-auto px-4 py-4 text-xs text-[#1b2d2a] flex flex-wrap items-center justify-between gap-2">
        <span>{copyright}</span>
        <span>v{packageJson.version}</span>
      </div>
    </footer>
  );
}
